"use client";

import clsx from "clsx";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import BrandImage from "@/components/common/BrandImage";
import CircleBackground from "@/components/common/backgrounds/CircleBackground";
import Login from "@/components/common/login";
import NavContainer from "@/components/common/navContainer";
import ProductAccessRealtimeSubscriber from "@/components/common/ProductAccessRealtimeSubscriber";
import { DEFAULT_BRAND_KEY } from "@/types/branding";
import { PublicBannerContextProvider } from "@/hooks/usePublicBanner";
import { useRouter } from "@bprogress/next";
import { useAuthContext } from "./AuthContextProvider";

// Component that uses useSearchParams - needs to be wrapped in Suspense
const NavContainerWithSearchParams = ({
  children,
  setShowUserManagement,
}: {
  children: React.ReactNode;
  setShowUserManagement: (value: boolean) => void;
}) => {
  const router = useRouter();
  const auth = useAuthContext();
  const searchParams = useSearchParams();

  const [initializedUserId, setInitializedUserId] = useState<string | null>(null);
  const [zIndex, setZIndex] = useState(100);
  const [loadingBrand] = useState<string>(
    () => (globalThis.window === undefined ? null : localStorage.getItem("brand")) ?? DEFAULT_BRAND_KEY
  );

  const hasError = !!searchParams.get("error");

  useEffect(() => {
    if (!auth.user || initializedUserId === auth.user.userId) {
      return;
    }

    if (hasError) {
      return;
    }

    setInitializedUserId(auth.user.userId);

    const userNeedsOrgSelection = auth.user && !auth.user.orgId;
    const userNeedsOnboarding = auth.user && !auth.user.agencyId;
    if ((userNeedsOrgSelection || userNeedsOnboarding) && !hasError) {
      router.push("/springboard");
    }
  }, [auth.user]);

  const isOnboarded: boolean = !!(auth.user?.agencyId && auth.user.orgId);
  let isLoading =
    (auth.isLoading ||
      (auth.isAuthenticated && !auth.user) ||
      (auth.user !== undefined && !isOnboarded)) &&
    !hasError;

  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => {
        setZIndex(-50);
      }, 300);
    }
  }, [isLoading]);

  return (
    <>
      {zIndex > 0 && (
        <div
          className={clsx("h-screen", "w-screen", "fixed", "text-800", {
            ["opacity-100"]: isLoading,
            [`opacity-0 transition-opacity ease-in z-[${zIndex}]`]: !isLoading && zIndex > 0,
          })}
          style={{
            transitionDuration: !isLoading && zIndex > 0 ? "300ms" : undefined,
          }}
        >
          <CircleBackground />
          <div className="flex justify-center items-center bg-background/70 min-h-screen">
            <BrandImage brand={loadingBrand} width={160} />
          </div>
        </div>
      )}
      {!isLoading && (
        <NavContainer setShowUserManagement={setShowUserManagement} hasError={hasError}>
          {auth.user !== undefined && !hasError ? (
            <>
              <ProductAccessRealtimeSubscriber />
              {children}
            </>
          ) : (
            <PublicBannerContextProvider>
              <Login />
            </PublicBannerContextProvider>
          )}
        </NavContainer>
      )}
    </>
  );
};

// Main provider component - removed Suspense wrapper for Next.js 16 compatibility
export const NavContainerProvider = ({
  children,
  setShowUserManagement,
}: {
  children: React.ReactNode;
  setShowUserManagement: (value: boolean) => void;
}) => {
  return (
    <NavContainerWithSearchParams setShowUserManagement={setShowUserManagement}>
      {children}
    </NavContainerWithSearchParams>
  );
};
